import {CommonModule} from '@angular/common';
import {Component} from '@angular/core';
import {ActivatedRoute, Router, RouterModule} from '@angular/router';
import {ButtonModule} from 'primeng/button';
import {PanelModule} from 'primeng/panel';
import {AuthService} from '../../../services/auth.service';
import {EkButtonComponent} from '@beta-foundations/ui-kit/buttons';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {InputTextModule} from 'primeng/inputtext';
import {EkCardModule} from '@beta-foundations/ui-kit/panel/card';
import {EkInputTextComponent} from '@beta-foundations/ui-kit/form/input-text';
import {PasswordModule} from 'primeng/password';
import {firstValueFrom} from 'rxjs';
import {SettingsService} from '../../services/settings.service';
import {UserService} from '../../services/user.service';

@Component({
    selector: 'app-login-page',
    standalone: true,
    imports: [
        CommonModule,
        RouterModule,
        ButtonModule,
        PanelModule,
        EkButtonComponent,
        TranslateModule,
        ReactiveFormsModule,
        InputTextModule,
        EkCardModule,
        EkInputTextComponent,
        PasswordModule
    ],
    templateUrl: './login-page.component.html',
    styleUrl: './login-page.component.scss'
})
export class LoginPageComponent {
    loginForm: FormGroup;
    recoveryForm: FormGroup;

    showRecoveryForm = false;
    isNewPasswordRequired = false;
    codeSent = false;

    loginError = '';
    loginSuccessMessage = '';
    passwordResetError = '';
    newPasswordError = '';

    isLoading = false;

    constructor(
        private authService: AuthService,
        private router: Router,
        private route: ActivatedRoute,
        private fb: FormBuilder,
        private translate: TranslateService,
        private settingsService: SettingsService,
        private userService: UserService
    ) {
        this.loginForm = this.fb.group({
            username: ['', [Validators.required]],
            password: ['', [Validators.required]]
        });

        this.recoveryForm = this.fb.group({
            recoveryUsername: ['', [Validators.required, Validators.email]],
            recoveryCode: ['', [Validators.required]],
            newPassword: ['', [Validators.required]],
            confirmNewPassword: ['', [Validators.required]]
        }, {validators: this.passwordMatchValidator});
    }

    private passwordMatchValidator(form: FormGroup) {
        const newPassword = form.get('newPassword')?.value;
        const confirmNewPassword = form.get('confirmNewPassword')?.value;
        if (!newPassword || !confirmNewPassword) {
            return null;
        }
        return newPassword === confirmNewPassword ? null : {passwordMismatch: true};
    }

    private toggleRecoveryValidators(enable: boolean): void {
        const recoveryUsernameControl = this.recoveryForm.get('recoveryUsername');
        const recoveryCodeControl = this.recoveryForm.get('recoveryCode');

        if (enable) {
            recoveryUsernameControl?.setValidators([Validators.required, Validators.email]);
            recoveryCodeControl?.setValidators([Validators.required]);
        } else {
            recoveryUsernameControl?.setValidators(null);
            recoveryCodeControl?.setValidators(null);
        }

        recoveryUsernameControl?.updateValueAndValidity();
        recoveryCodeControl?.updateValueAndValidity();
    }

    iHaveACode(event: Event): void {
        event.preventDefault();

        const loginUsername = this.loginForm.get('username')?.value;
        if (loginUsername) {
            this.recoveryForm.patchValue({ recoveryUsername: loginUsername });
        }

        this.toggleRecoveryValidators(true);

        this.showRecoveryForm = true;
        this.codeSent = true;

        this.loginError = '';
        this.passwordResetError = '';
        this.newPasswordError = '';
    }

    async login(): Promise<void> {
        this.loginError = '';
        this.loginSuccessMessage = '';
        if (this.loginForm.invalid) {
            this.loginForm.markAllAsTouched();
            return;
        }

        this.isLoading = true;
        const {username, password} = this.loginForm.value;

        try {
            const output = await this.authService.loginWithCredentials(username, password);

            // v5: user.challengeName === 'NEW_PASSWORD_REQUIRED'
            if (output.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
                this.isNewPasswordRequired = true;
                this.recoveryForm.patchValue({recoveryUsername: username});

                this.toggleRecoveryValidators(false);

                this.loginError = this.translate.instant('LOGIN_PAGE.ERRORS.PASSWORD_RESET_REQUIRED');
            } else if (output.isSignedIn) {
                await firstValueFrom(this.settingsService.loadAllSettings());
                const returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/home';
                this.router.navigateByUrl(returnUrl);
            } else {
                this.loginError = this.getErrorMessage(undefined);
            }
        } catch (error: any) {
            console.error('Errore durante il login:', error);

            if (error?.name === 'PasswordResetRequiredException') {
                this.recoveryForm.patchValue({ recoveryUsername: username });
                this.showRecoveryForm = true;
                this.codeSent = true;

                this.toggleRecoveryValidators(true);

                this.passwordResetError = this.translate.instant('LOGIN_PAGE.ERRORS.PASSWORD_RESET_REQUIRED_BY_ADMIN') || 'Password reset required by admin.';
            } else {
                this.loginError = this.getErrorMessage(error?.name);
            }
        } finally {
            this.isLoading = false;
        }
    }

    async requestPasswordReset(): Promise<void> {
        this.passwordResetError = '';
        const recoveryUsernameControl = this.recoveryForm.get('recoveryUsername');

        if (recoveryUsernameControl?.invalid) {
            recoveryUsernameControl.markAsTouched();
            return;
        }

        this.isLoading = true;
        const {recoveryUsername} = this.recoveryForm.value;

        try {
            await this.authService.forgotPassword(recoveryUsername);
            this.codeSent = true;
        } catch (error: any) {
            console.error('Errore nella richiesta di recupero:', error);
            this.passwordResetError = this.getErrorMessage(error?.name);
        } finally {
            this.isLoading = false;
        }
    }

    async submitNewPassword(): Promise<void> {
        this.newPasswordError = '';
        if (this.recoveryForm.invalid) {
            this.recoveryForm.markAllAsTouched();
            return;
        }

        this.isLoading = true;
        const {recoveryUsername, recoveryCode, newPassword} = this.recoveryForm.value;

        try {
            await this.authService.forgotPasswordSubmit(
                recoveryUsername,
                recoveryCode,
                newPassword
            );
            this.showRecoveryForm = false;
            this.codeSent = false;

            this.toggleRecoveryValidators(true);

            this.loginForm.patchValue({username: recoveryUsername});
            this.recoveryForm.reset();
            this.loginSuccessMessage = this.translate.instant('LOGIN_PAGE.MESSAGES.PASSWORD_CHANGED_SUCCESSFULLY');
        } catch (error: any) {
            console.error('Errore nella modifica della password:', error);
            this.newPasswordError = this.getErrorMessage(error?.name);
        } finally {
            this.isLoading = false;
        }
    }

    async submitNewPasswordChallenge(): Promise<void> {
        this.newPasswordError = '';
        if (this.recoveryForm.invalid) {
            this.recoveryForm.markAllAsTouched();
            return;
        }

        this.isLoading = true;
        const {newPassword} = this.recoveryForm.value;

        try {
            await this.authService.completeNewPasswordChallenge(newPassword);

            const token = await this.authService.getIdToken();
            if (token) {
                await firstValueFrom(this.userService.firstAccess(token));
            }

            this.isNewPasswordRequired = false;

            this.toggleRecoveryValidators(true);

            this.recoveryForm.reset();
            this.loginSuccessMessage = this.translate.instant('LOGIN_PAGE.MESSAGES.PASSWORD_CHANGED_SUCCESSFULLY');
            this.loginError = '';
            const returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/home';
            this.router.navigateByUrl(returnUrl);
        } catch (error: any) {
            console.error('Errore nella modifica della password al primo accesso:', error);
            this.newPasswordError = this.getErrorMessage(error?.name);
            this.loginSuccessMessage = '';
        } finally {
            this.isLoading = false;
        }
    }

    toggleRecoveryForm(event: Event): void {
        event.preventDefault();

        this.toggleRecoveryValidators(true);

        this.showRecoveryForm = !this.showRecoveryForm;
        this.codeSent = false;
        this.isNewPasswordRequired = false;
        this.loginForm.reset();
        this.recoveryForm.reset();
        this.loginError = '';
        this.loginSuccessMessage = '';
        this.passwordResetError = '';
        this.newPasswordError = '';
    }

    private getErrorMessage(errorCode: string | undefined): string {
        switch (errorCode) {
            case 'UserNotFoundException':
                return this.translate.instant('LOGIN_PAGE.ERRORS.USER_NOT_FOUND');
            case 'NotAuthorizedException':
                return this.translate.instant('LOGIN_PAGE.ERRORS.INVALID_CREDENTIALS');
            case 'CodeMismatchException':
                return this.translate.instant('LOGIN_PAGE.ERRORS.WRONG_CODE');
            case 'InvalidPasswordException':
                return this.translate.instant('LOGIN_PAGE.ERRORS.INVALID_PASSWORD');
            case 'InvalidParameterException':
                return this.translate.instant('LOGIN_PAGE.ERRORS.INVALID_PARAMETER');
            case 'PasswordMismatch':
                return this.translate.instant('LOGIN_PAGE.ERRORS.PASSWORD_MISMATCH');
            case 'LimitExceededException':
                return this.translate.instant('LOGIN_PAGE.ERRORS.LIMIT_EXCEEDED');
            case 'PasswordResetRequiredException':
                return this.translate.instant('LOGIN_PAGE.ERRORS.PASSWORD_RESET_REQUIRED');
            case 'SAMLLoginError':
                return this.translate.instant('LOGIN_PAGE.ERRORS.SAML_LOGIN_FAILED');
            default:
                return this.translate.instant('LOGIN_PAGE.ERRORS.GENERIC');
        }
    }

    get loginUsername() { return this.loginForm.get('username'); }
    get loginPassword() { return this.loginForm.get('password'); }
    get recoveryUsername() { return this.recoveryForm.get('recoveryUsername'); }
    get recoveryCode() { return this.recoveryForm.get('recoveryCode'); }
    get newPassword() { return this.recoveryForm.get('newPassword'); }
    get confirmNewPassword() { return this.recoveryForm.get('confirmNewPassword'); }
}